import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import { getBankerAssignedDeals } from "../../../services/banker.service";
import BankerDealsCardView from "./BankerDealsCardView";
import BankerDealsTableView from "./BankerDealsTableView";
import ExpressInterestDialog from "./ExpressInterestDialog";
import useExpressInterest from "./useExpressInterest";
import TermsSheet from "./termSheet";
import {LoanStatus} from "../../../constants/general.constant";
import { Deal } from "../../../validation/customer.validation";

const dealTypes = ["purchase", "refinance", "startup"];

const BankerDeals: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedDealTypes, setSelectedDealTypes] = useState<string[]>([]);
  const [anchorElDealType, setAnchorElDealType] = useState<null | HTMLElement>(null);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [anchorElSort, setAnchorElSort] = useState<null | HTMLElement>(null);
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [interestChoice, setInterestChoice] = useState<string>("");
  const [view, setView] = useState<"table" | "grid">(isMobile ? "grid" : "table");

  const updateDealStatus = (dealId: string, status: string) => {
    setDeals(prevDeals =>
      prevDeals.map(deal =>
        deal.id === dealId
          ? {
              ...deal,
              assignedBanks: deal.assignedBanks.map((bank: any, index: number) =>
                index === 0 ? { ...bank, interestStatus: status } : bank
              ),
            }
          : deal
      )
    );
  };

  const { handleInterest, snackbarOpen, snackbarMessage, snackbarSeverity, handleSnackbarClose } = useExpressInterest(updateDealStatus);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (selectedDealTypes.length > 0) params.dealType = selectedDealTypes.join(",");
      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;
      const data = await getBankerAssignedDeals(params);
      setDeals(data || []);
    } catch (error) {
      console.error("Error fetching deals:", error);
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, [search, selectedDealTypes, sortBy, sortOrder]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchDeals();
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [fetchDeals]);

  const handleDealTypeClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElDealType(event.currentTarget);
  };

  const handleDealTypeClose = () => {
    setAnchorElDealType(null);
  };

  const handleDealTypeChange = (type: string) => {
    setSelectedDealTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleInterestClick = (deal: Deal) => {
    setSelectedDeal(deal);
    setInterestDialogOpen(true);
  };

  const handleInterestSubmit = async () => {
    if (!selectedDeal || !interestChoice || !selectedDeal.id) return;
    if (interestChoice === "interested") {
      // Open terms sheet for interested bankers
      setTermsSheetOpen(true);
    } else {
      const status = LoanStatus.REJECTED;
      await handleInterest(selectedDeal.id, status);
      setInterestDialogOpen(false);
      setInterestChoice("");
      setSelectedDeal(null);
    }
  };

  const [termsSheetOpen, setTermsSheetOpen] = useState(false);

  const handleTermsSheetAccept = async (dealId: string) => {
    await handleInterest(dealId, LoanStatus.APPROVED);
    setTermsSheetOpen(false);
    setInterestDialogOpen(false);
    setInterestChoice("");
    setSelectedDeal(null);
  };

  const handleTermsSheetClose = () => {
    setTermsSheetOpen(false);
  };

  return (
    <div className="p-2 mx-auto">
      {/* View Toggle */}
      <div className="mb-4 flex justify-end">
        <div className="flex items-center gap-2 rounded-xl border border-gray-300 overflow-hidden">
          <IconButton
            onClick={() => setView("grid")}
            className={`!p-2 ${view === "grid" ? "bg-gray-100" : ""}`}
          >
            <ViewModuleIcon
              className={view === "grid" ? "text-black" : "text-gray-500"}
            />
          </IconButton>
          <IconButton
            onClick={() => setView("table")}
            className={`!p-2 ${view === "table" ? "bg-gray-100" : ""}`}
          >
            <ViewListIcon
              className={view === "table" ? "text-black" : "text-gray-500"}
            />
          </IconButton>
        </div>
      </div>

      {/* Search and Filters */}
      <div className={`flex ${isMobile ? 'flex-col' : 'flex-wrap'} gap-2 mb-6`}>
        <TextField
          variant="outlined"
          placeholder="Search by loan name, customer name, or city/zip"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: isMobile ? '100%' : 300 }}
        />
        <Button
          variant="outlined"
          sx={{ borderRadius: 2, textTransform: "none" }}
          onClick={handleDealTypeClick}
        >
          Deal Type
        </Button>

        <Menu
          anchorEl={anchorElDealType}
          open={Boolean(anchorElDealType)}
          onClose={handleDealTypeClose}
        >
          {dealTypes.map((type) => (
            <MenuItem key={type} onClick={() => handleDealTypeChange(type)}>
              <Checkbox checked={selectedDealTypes.includes(type)} />
              <ListItemText primary={type} />
            </MenuItem>
          ))}
        </Menu>

        <Button
          variant="outlined"
          sx={{ borderRadius: 2, textTransform: "none" }}
          onClick={(event) => setAnchorElSort(event.currentTarget)}
        >
          Sort By
        </Button>

        <Menu
          anchorEl={anchorElSort}
          open={Boolean(anchorElSort)}
          onClose={() => setAnchorElSort(null)}
        >
          <MenuItem onClick={() => { setSortBy('loanSize'); setSortOrder('ASC'); setAnchorElSort(null); }}>
            Size (Low to High)
          </MenuItem>
          <MenuItem onClick={() => { setSortBy('loanSize'); setSortOrder('DESC'); setAnchorElSort(null); }}>
            Size (High to Low)
          </MenuItem>
          <MenuItem onClick={() => { setSortBy('maxLTV'); setSortOrder('ASC'); setAnchorElSort(null); }}>
            LTV (Low to High)
          </MenuItem>
          <MenuItem onClick={() => { setSortBy('maxLTV'); setSortOrder('DESC'); setAnchorElSort(null); }}>
            LTV (High to Low)
          </MenuItem>
          <MenuItem onClick={() => { setSortBy('minDSCRatio'); setSortOrder('ASC'); setAnchorElSort(null); }}>
            DSC (Low to High)
          </MenuItem>
          <MenuItem onClick={() => { setSortBy('minDSCRatio'); setSortOrder('DESC'); setAnchorElSort(null); }}>
            DSC (High to Low)
          </MenuItem>
          <MenuItem onClick={() => { setSortBy('createdAt'); setSortOrder('ASC'); setAnchorElSort(null); }}>
            Created At (Oldest First)
          </MenuItem>
          <MenuItem onClick={() => { setSortBy('createdAt'); setSortOrder('DESC'); setAnchorElSort(null); }}>
            Created At (Newest First)
          </MenuItem>
        </Menu>
      </div>

      {/* Active Filters */}
      {(selectedDealTypes.length > 0 || sortBy) && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {selectedDealTypes.map(type => (
              <Chip
                key={type}
                label={`Deal Type: ${type}`}
                onDelete={() => handleDealTypeChange(type)}
                size="medium"
                color="primary"
                variant="outlined"
              />
            ))}
            {sortBy && (
              <Chip
                label={`Sort: ${sortBy === 'loanSize' ? 'Size' : sortBy === 'maxLTV' ? 'LTV' : sortBy === 'minDSCRatio' ? 'DSC' : sortBy === 'createdAt' ? 'Created At' : sortBy} (${sortOrder === 'ASC' ? 'Low to High' : 'High to Low'})`}
                onDelete={() => { setSortBy(''); setSortOrder('ASC'); }}
                size="medium"
                color="secondary"
                variant="outlined"
              />
            )}
          </div>
        </div>
      )}

      {/* Deal Views */}
      {view === "grid" ? (
        <BankerDealsCardView deals={deals} loading={loading} handleInterestClick={handleInterestClick} updateDealStatus={updateDealStatus} />
      ) : (
        <BankerDealsTableView deals={deals} loading={loading} handleInterestClick={handleInterestClick} updateDealStatus={updateDealStatus} />
      )}

      <ExpressInterestDialog
        open={interestDialogOpen}
        onClose={() => setInterestDialogOpen(false)}
        dealName={selectedDeal?.loanName || ""}
        interestChoice={interestChoice}
        onInterestChoiceChange={setInterestChoice}
        onSubmit={handleInterestSubmit}
      />

      <TermsSheet
        open={termsSheetOpen}
        deal={selectedDeal}
        onClose={handleTermsSheetClose}
        onAccept={handleTermsSheetAccept}
      />

      {/* Snackbar for notifications */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default BankerDeals;
